import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    CheckboxFormInput,
    SelectFormInput,
    TextFormInput,
} from "src/components/form";
import { TrialInstitutionFinanceBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "src/helpers/lookupHelper";
import useNavigate from "src/hooks/useNavigate";
import { RegistrationFormComponent } from "src/interfaces/forms/IRegistrationComponent";
import { ITrialInstitutionFinanceReduxFormState } from "src/reducers/reactReduxForms/trialInstitutionFinance";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialInstitutionFinanceActions from "../../../actions/trialInstitutionFinance";
import { NotificationAlert } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { TrialInstitutionSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./TrialInstitutionFinancePage.scss";

interface TrialInstitutionFinancePageProps {
    form: Dtos.TrialInstitutionFinance & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ITrialInstitutionFinanceReduxFormState;
    trialInstitutionFinance: Dtos.TrialInstitutionFinance;
    lookups: Dtos.Lookup[];
    loadingTrialInstitutionFinance: boolean;
    loadTrialInstitutionFinanceSuccess: boolean;
    loadTrialInstitutionFinanceFailure: boolean;
    loadTrialInstitutionFinanceErrorCode: string;
    savingTrialInstitutionFinance: boolean;
    saveTrialInstitutionFinanceSuccess: boolean;
    saveTrialInstitutionFinanceFailure: boolean;
    validatingTrialInstitutionFinance: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialInstitutionFinance: TrialInstitutionFinanceActions.ITrialInstitutionFinanceClearActionCreator;
    loadTrialInstitutionFinance: TrialInstitutionFinanceActions.ITrialInstitutionFinanceLoadByTrialInstitutionIdActionCreator;
    saveTrialInstitutionFinance: TrialInstitutionFinanceActions.ITrialInstitutionFinanceSaveActionCreator;
    validateTrialInstitutionFinance: TrialInstitutionFinanceActions.ITrialInstitutionFinanceValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trialInstitutionFinance";

const TrialInstitutionFinancePage: FC<TrialInstitutionFinancePageProps> = ({
    form,
    formState,
    formProperties,
    trialInstitutionFinance,
    lookups,
    loadingTrialInstitutionFinance,
    loadTrialInstitutionFinanceFailure,
    loadTrialInstitutionFinanceErrorCode,
    savingTrialInstitutionFinance,
    saveTrialInstitutionFinanceSuccess,
    saveTrialInstitutionFinanceFailure,
    validatingTrialInstitutionFinance,
    validationFailures,
    permissions,
    clearTrialInstitutionFinance,
    loadTrialInstitutionFinance,
    saveTrialInstitutionFinance,
    validateTrialInstitutionFinance,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Trial Finance
    useEffect(() => {
        if (trialInstitutionIdNumber && !trialInstitutionFinance) {
            setBreadcrumbs(
                TrialInstitutionFinanceBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber
                )
            );

            loadTrialInstitutionFinance(trialInstitutionIdNumber);
        } else if (trialInstitutionFinance && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        trialInstitutionIdNumber,
        trialInstitutionFinance,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadTrialInstitutionFinance,
        TrialInstitutionFinanceBreadcrumbs,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialInstitutionFinanceFailure) {
            navigate(`/error/${loadTrialInstitutionFinanceErrorCode}`);
        }
    }, [
        loadTrialInstitutionFinanceFailure,
        loadTrialInstitutionFinanceErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            trialInstitutionIdNumber &&
            trialInstitutionFinance?.trialInstitution
        ) {
            setDrawerItems(
                TrialInstitutionSubMenu(
                    trialInstitutionFinance?.trialInstitution,
                    permissions
                )
            );
            setBreadcrumbs(
                TrialInstitutionFinanceBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    trialInstitutionFinance.trialInstitution?.trial
                        ?.displayName,
                    trialInstitutionFinance.trialInstitution?.institution?.name
                )
            );
        }
    }, [
        trialInstitutionIdNumber,
        trialInstitutionFinance,
        setBreadcrumbs,
        TrialInstitutionFinanceBreadcrumbs,
    ]);

    // Reload trial institution finance after saving
    useEffect(() => {
        if (
            !savingTrialInstitutionFinance &&
            saveTrialInstitutionFinanceSuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial institution finance saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                const url = `/trials/${trialIdNumber}/institutions`;
                navigate(url);
            } else if (!trialInstitutionIdNumber) {
                const url = `/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/finance`;
                navigate(url);
                clearTrialInstitutionFinance();
            }
        }
    }, [
        savingTrialInstitutionFinance,
        saveTrialInstitutionFinanceSuccess,
        trialInstitutionIdNumber,
        navigate,
        saveAndReturn,
        clearTrialInstitutionFinance,
    ]);

    // Failed saving
    useEffect(() => {
        if (
            !savingTrialInstitutionFinance &&
            saveTrialInstitutionFinanceFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialInstitutionFinance, saveTrialInstitutionFinanceSuccess]);

    // Cleanup effect to clear the trial institution finance on component unmount
    useEffect(() => {
        return () => {
            clearTrialInstitutionFinance();
            resetForm(reduxFormName);
        };
    }, [clearTrialInstitutionFinance, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialInstitutionFinance =
                {} as Dtos.TrialInstitutionFinance;

            if (!data.trialInstitutionId) {
                changes.trialInstitutionId = trialInstitutionIdNumber;
            }

            await saveTrialInstitutionFinance(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialInstitutionFinance) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const url = "/trials";
        navigate(url);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.TrialInstitutionFinance =
            {} as Dtos.TrialInstitutionFinance;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialInstitutionFinance }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialInstitutionFinance =
                {} as Dtos.TrialInstitutionFinance;

            if (
                form &&
                hasBeenSubmitted &&
                !validatingTrialInstitutionFinance
            ) {
                await validateTrialInstitutionFinance(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                trialInstitutionIdNumber,
                null,
                permissions,
                Dtos.Permission.TrialInstitutionFinanceUpdate,
                Dtos.Permission.TrialInstitutionFinanceCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialInstitutionFinance ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionFinance",
                                        {
                                            overrideFundingEmail: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Override Institution Email Address",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionFinance",
                                        {
                                            fundingEmail: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overrideFundingEmail,
                                                inputProps: {
                                                    value: !form.overrideFundingEmail
                                                        ? trialInstitutionFinance
                                                                ?.trialInstitution
                                                                ?.institution
                                                                ?.institutionFinance
                                                                ?.fundingEmailAddress
                                                        : form.fundingEmail,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionFinance",
                                        {
                                            accountNumber: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionFinance",
                                        {
                                            bsbSwift: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionFinance",
                                        {
                                            siteCostCentre: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionFinance",
                                        {
                                            overridePaymentDetails: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Override Payment Details",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionFinance",
                                        {
                                            abn: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overridePaymentDetails,
                                                inputProps: {
                                                    value: !form.overridePaymentDetails
                                                        ? trialInstitutionFinance
                                                                ?.trialInstitution
                                                                ?.institution?.abn
                                                        : form.abn,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionFinance",
                                        {
                                            taxInvoiceName: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overridePaymentDetails,
                                                inputProps: {
                                                    value: !form.overridePaymentDetails
                                                        ? trialInstitutionFinance
                                                                ?.trialInstitution
                                                                ?.institution
                                                                ?.institutionFinance
                                                                ?.taxInvoiceName
                                                        : form.taxInvoiceName,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialInstitutionFinance",
                                        {
                                            quickbooksTaxCodeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overridePaymentDetails,
                                                inputProps: {
                                                    value: !form.overridePaymentDetails
                                                        ? trialInstitutionFinance
                                                                ?.trialInstitution
                                                                ?.institution
                                                                ?.institutionFinance
                                                                ?.quickbooksTaxCodeId
                                                        : form.quickbooksTaxCodeId,
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "InstitutionFinance",
                                                                "quickbooksTaxCodeId",
                                                                lookups
                                                            ),
                                                            form.quickbooksTaxCodeId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "trialInstitutionFinance",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        permissions,
        savingTrialInstitutionFinance,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        trialInstitutionFinance,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={
                loadingTrialInstitutionFinance || savingTrialInstitutionFinance
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialInstitutionFinancePageProps> => {
    let trialInstitutionFinance: Dtos.TrialInstitutionFinance | undefined = !(
        state.trialInstitutionFinance.formData instanceof Array
    )
        ? state.trialInstitutionFinance.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialInstitutionFinance: trialInstitutionFinance!,
        loadingTrialInstitutionFinance:
            state.trialInstitutionFinance.loadState &&
            state.trialInstitutionFinance.loadState.status ==
                RequestState.Pending,
        loadTrialInstitutionFinanceSuccess:
            state.trialInstitutionFinance.loadState &&
            state.trialInstitutionFinance.loadState.status ==
                RequestState.Success,
        loadTrialInstitutionFinanceFailure:
            state.trialInstitutionFinance.loadState &&
            state.trialInstitutionFinance.loadState.status ==
                RequestState.Failure,
        loadTrialInstitutionFinanceErrorCode:
            state.trialInstitutionFinance.loadState.errorCode,
        savingTrialInstitutionFinance:
            state.trialInstitutionFinance.saveState &&
            state.trialInstitutionFinance.saveState.status ==
                RequestState.Pending,
        saveTrialInstitutionFinanceSuccess:
            state.trialInstitutionFinance.saveState &&
            state.trialInstitutionFinance.saveState.status ==
                RequestState.Success,
        saveTrialInstitutionFinanceFailure:
            state.trialInstitutionFinance.saveState &&
            state.trialInstitutionFinance.saveState.status ==
                RequestState.Failure,
        validatingTrialInstitutionFinance:
            state.trialInstitutionFinance.validateState &&
            state.trialInstitutionFinance.validateState.status ==
                RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.trialInstitutionFinance.lookups,
        form: state.reduxForms.trialInstitutionFinance,
        formState: state.trialInstitutionFinance.formState,
        formProperties: state.trialInstitutionFinance.formProperties,
        reduxFormState: state.reduxForms.formState.trialInstitutionFinance,
        validationFailures: trialInstitutionFinance
            ? state.trialInstitutionFinance.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialInstitutionFinancePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialInstitutionFinance: bindActionCreators(
            TrialInstitutionFinanceActions.LoadTrialInstitutionFinanceByTrialInstitutionId,
            dispatch
        ),
        clearTrialInstitutionFinance: bindActionCreators(
            TrialInstitutionFinanceActions.Clear,
            dispatch
        ),
        saveTrialInstitutionFinance: bindActionCreators(
            TrialInstitutionFinanceActions.SaveTrialInstitutionFinance,
            dispatch
        ),
        validateTrialInstitutionFinance: bindActionCreators(
            TrialInstitutionFinanceActions.ValidateTrialInstitutionFinance,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialInstitutionFinancePage);
